import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { BuilderWebsiteEvents } from "builder-website/src/constants";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOption, DocOptionLock } from "building-types/options/optionSet";
import type { SerializedOptionSetEntityRelationship } from "building-types/options/optionSetEntityRelationsTypes";
// ============================================================================
// Patch Types - extracted from useShowroomPatch.ts to break circular deps
// ============================================================================
import type {
  OptionOverrides,
  OptionSetOverride,
  ShowroomIntroOptionOverride,
  ShowroomIntroOptionSetOverride,
} from "building-types/options/optionTemplateTypes";
import type { Mutable } from "common/utils/types";
import type { InstanceIdPath } from "doc/options/componentInstanceTree";
import type { OptionSetState } from "doc/options/optionSetState";
import type { ReactNode } from "react";

// Payload sent from client JavaScript to Showroom's Plan View
export interface BuilderOptionChoicesUpdatePayload {
  optionSets?: OptionChoices;
  options?: {
    [optionId: string]: boolean;
  };
}

/** A secondary-level accordion group within a primary category page, e.g. "Elevations". */
export interface ShowroomSecondaryCategoryGroup {
  /** Stable secondary level key, e.g. "ELEVATIONS". */
  secondaryKey: string;
  /** Display name, e.g. "Elevations". */
  secondaryName: string;
  optionSets: ShowroomOptionSet[];
}

/** A primary sales-category page, e.g. "Design", holding its secondary groups. */
export interface ShowroomPrimaryCategoryPage {
  /** Stable primary level key, e.g. "DESIGN". */
  primaryKey: string;
  /** Display name, e.g. "Design". */
  primaryName: string;
  secondaryGroups: ShowroomSecondaryCategoryGroup[];
}

/**
 * How an option journey (or a tree node's options) is laid out. Intro uses "flat" to preserve
 * exact globalSortOrder; the sales/ops configure flow uses "category" for one-primary-per-page.
 */
export type ShowroomOptionLayout =
  | { kind: "flat"; optionSets: ShowroomOptionSet[] }
  | { kind: "category"; pages: ShowroomPrimaryCategoryPage[] };

/** A node in the showroom component instance tree with ShowroomOptionSet payloads */
export interface ShowroomInstanceNode {
  id: string;
  /** Full path of instance IDs from root, e.g., "root/abc123/def456" */
  path: InstanceIdPath;
  /** Display name (e.g., "Unit A", "Building") */
  name: string;
  /** null for root node */
  componentId: string | null;
  /** The reporting unit this instance belongs to, if any. Used for unit-based filtering. */
  reportingUnitId: string | null;
  /** Option layout for this instance: flat list (intro) or category pages (sales/ops). */
  optionLayout: ShowroomOptionLayout;
  children: ShowroomInstanceNode[];
}

// A flattened optionSet for use in Showroom UI
export type ShowroomOptionSet = {
  disconnected?: SerializedOptionSetEntityRelationship;
  options: ShowroomOption[];
  uiType?: "ELEVATION" | "PALETTE";
  isBoolean: boolean;
  category?: string; // TBD
  disabled?: boolean;
  hidden?: boolean;
  globalSortOrder: number; // required
  reportingUnitIdsUsage?: string[];
} & Pick<OptionSetState, "currentValue"> &
  // options are converted to an array
  // make showroom config fields mutable in order to set defaults
  Mutable<Omit<OptionSetState["optionSet"], "options" | "disabled" | "globalSortOrder">> &
  Pick<DocOptionLock, "disconnectConditionOptions">;

export interface ShowroomOption extends Mutable<Omit<DocOption, "disabled">> {
  // adding optionId from optionSet.options[optionId]
  optionId: string;
  optionSetId: string;
  thumbnailUrl?: string;
  priceDisplay?: string;
  selected: boolean;
  tooltip?: ReactNode;
  hidden?: boolean;
  disabled?: boolean;
  disconnected?: boolean;
}

// Extend as needed for edit state

export type PlanData = ClientHomePlanBaseData & {
  state?: {
    isPatched?: boolean;
    // for memo/cache busting
    lastPatchedAt?: number;
    patchedPlanFields?: Partial<{
      [Property in keyof ClientHomePlanBaseData]: Partial<{
        [Field in keyof ClientHomePlanBaseData[Property]]: boolean;
      }>;
    }>;
  };
};

// These are not sent to builder analytics
export const internalShowroomEvents: ShowroomIntroEvent[] = [
  "showroom-intro:performance",
  "showroom-intro:performance-worker",
  "showroom-intro:performance-server",
  "showroom-intro:option-selected-performance",
  "showroom-intro:option-selected-performance-worker",
  "showroom-intro:option-selected-performance-server",
  "showroom-intro:3d viewer crashed",
];
export type ShowroomIntroEvent =
  // sent to builder analytics & internal
  | "showroom-intro:loaded"
  | "showroom-intro:selected option"
  | "showroom-intro:clicked CTA"
  | "showroom-intro:submitted CTA"
  | "showroom-intro:clicked share button"
  | "showroom-intro:changed viewer - 3d"
  | "showroom-intro:changed viewer - exterior"
  | "showroom-intro:changed viewer - interior"
  | "showroom-intro:changed viewer - floorplan"
  // builder analytics only
  | BuilderWebsiteEvents
  // internal only
  | "showroom-intro:3d viewer crashed"
  | "showroom-intro:performance"
  | "showroom-intro:performance-worker"
  | "showroom-intro:performance-server"
  | "showroom-intro:option-selected-performance"
  | "showroom-intro:option-selected-performance-worker"
  | "showroom-intro:option-selected-performance-server"
  | "showroom-intro[edit mode]:saved changes";

export const DEBUG_SHOWROOM_INTRO_PATH = "/debug/builder-website/showroom-intro";

/**
 * A lookup table of options <> hashes for URL-based option state.
 * Extracted here to avoid circular dependency with showroomState.ts
 */
export interface UrlOptionMap {
  hashToOption: {
    [hash: string]: {
      optionSetId: string;
      optionId: string;
    };
  };
  optionKeyToHash: {
    [optionKey: string]: string;
  };
}

/**
 * Plan key type extracted to break circular dependencies.
 */
export type ShowroomPlanKey = "masterPlan" | "communityPlan" | "lotCommunityPlan";

export interface PatchPlanOverrides {
  type: "overrides";
  planKey: ShowroomPlanKey;
  field: string;
  optionSetId: string;
  optionId?: string;
  value?: OptionSetOverride[keyof OptionSetOverride] | OptionOverrides[keyof OptionOverrides];
  clearOverride?: boolean;
}

export interface PatchShowroomIntroOverrides {
  type: "showroomIntroOverrides";
  planKey: ShowroomPlanKey;
  field: string;
  optionSetId: string;
  optionId?: string;
  value?:
    | ShowroomIntroOptionSetOverride[keyof ShowroomIntroOptionSetOverride]
    | ShowroomIntroOptionOverride[keyof ShowroomIntroOptionOverride];
  clearOverride?: boolean;
}

export interface PatchCommunityPlan {
  type: "communityPlan";
  field: string;
  value: PlanData["communityPlan"][keyof PlanData["communityPlan"]];
}

export interface PatchMasterPlan {
  type: "masterPlan";
  field: string;
  value: PlanData["masterPlan"][keyof PlanData["masterPlan"]];
}

export interface PatchCommunity {
  type: "community";
  field: string;
  value: NonNullable<PlanData["community"]>[keyof NonNullable<PlanData["community"]>];
}

export interface PatchLotCommunityPlan {
  type: "lotCommunityPlan";
  field: string;
  value: NonNullable<PlanData["lotCommunityPlan"]>[keyof NonNullable<PlanData["lotCommunityPlan"]>];
}

type ShowroomThemeConfigNonNullable = NonNullable<
  NonNullable<PlanData["builder"]["salesConfig"]>["showroomThemeConfig"]
>;

export interface PatchShowroomThemeConfig {
  type: "showroomThemeConfig";
  field: string;
  value: ShowroomThemeConfigNonNullable[keyof ShowroomThemeConfigNonNullable];
}

type ShowroomIntroConfigNonNullable = NonNullable<
  NonNullable<PlanData["builder"]["salesConfig"]>["showroomIntroConfig"]
>;

export interface PatchShowroomIntroConfig {
  type: "showroomIntroConfig";
  field: string;
  value: ShowroomIntroConfigNonNullable[keyof ShowroomIntroConfigNonNullable];
}

export type ShowroomPatchField =
  | PatchMasterPlan
  | PatchCommunityPlan
  | PatchPlanOverrides
  | PatchShowroomIntroOverrides
  | PatchCommunity
  | PatchLotCommunityPlan
  | PatchShowroomThemeConfig
  | PatchShowroomIntroConfig;

export type ShowroomPatch = {
  version: number;
  data: ShowroomPatchField | ShowroomPatchField[];
};

/**
 * Gets the correct overrides key based on the plan key.
 * Extracted to break circular dependency between useShowroomAPI and useShowroomPlanData.
 */
export function getOverridesKey(planKey: ShowroomPlanKey) {
  switch (planKey) {
    case "lotCommunityPlan":
      return "optionSetOverrides";
    default:
      return "overrides";
  }
}

export type ShowroomGenerationMode = "main" | "worker" | "server";
